import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { reportApi } from '../../../api/report/reportApi';
import {
    IDriverLoadDetail,
    IEarnings,
    ISettlementReport,
} from '../../../api/report/interface/ISettlementReport';
import { APIStatus, APIStatusOK } from '../../../api/apiError';
import { SettlementReportData } from '../components/InvoiceMobileReport/GenerateReport/components/SettlementsReportDetails/interfaces/ISettlementsReportData';
import { dispatchManagerFeatures } from '../../../featureFlags/dispatchManagerFeatures';
import { NullableDate } from '../../../utils/interfaces';
import { dateFormatter } from '../../../utils/functions';

const { isShowSettlementReportModal, isShowOldSettlementReport } =
    dispatchManagerFeatures;

export const fetchSettlementReport = createAsyncThunk(
    'fetchSettlementReport',
    async (
        {
            startDate,
            endDate,
            driverLoadIds,
        }: {
            startDate?: NullableDate;
            endDate?: NullableDate;
            driverLoadIds?: number[] | null;
        },
        { rejectWithValue, dispatch },
    ) => {
        try {
            let response;
            const isAgarLogin =
                localStorage.getItem('tenantId')?.toLowerCase() ===
                    process.env.REACT_APP_AGAR_TENANT_ID?.toLowerCase() ||
                sessionStorage.getItem('tenantId')?.toLowerCase() ===
                    process.env.REACT_APP_AGAR_TENANT_ID?.toLowerCase();

            if (
                (isAgarLogin && isShowOldSettlementReport) ||
                !isShowSettlementReportModal
            ) {
                const formattedStartDate = dateFormatter(startDate || '');
                const formattedEndDate = dateFormatter(endDate || '');
                response = await reportApi.fetchSettlementReport({
                    startDate: formattedStartDate,
                    endDate: formattedEndDate,
                });
            } else {
                response = await reportApi.fetchNewSettlementReport({
                    driverLoadIds,
                });
            }

            const formattedSettlementReportDetail =
                getFormattedSettlementReportDetail(response);

            dispatch(
                setSettlementReportDetail(formattedSettlementReportDetail),
            );

            return formattedSettlementReportDetail;
        } catch {
            return rejectWithValue('error');
        }
    },
);

const getFormattedSettlementReportDetail = (
    data: ISettlementReport[],
): SettlementReportData => {
    return data?.map((report: ISettlementReport) => ({
        driverName: report.driverName,
        driverId: report.driverId,
        truckingCompanyName: report.truckingCompanyName,
        overallTotalEarnings: report.overallTotalEarnings?.toFixed(2),
        driverLoadsDetail: report.driverLoadsDetail?.map(
            (load: IDriverLoadDetail) => ({
                driverLoadId: load.driverLoadId,
                isRateTypePerCWT: load.isRateTypePerCWT,
                ownerOperatorPercentage: load.ownerOperatorPercentage,
                loadDetail: [
                    {
                        name: 'loadNumber',
                        value: load.loadNumber,
                        columnIndex: 1,
                    },
                    {
                        name: 'pickupDate',
                        value: load.pickupDate?.replaceAll('-', '/'),
                        columnIndex: 2,
                    },
                    {
                        name: 'dropoffDate',
                        value: load.dropoffDate?.replaceAll('-', '/'),
                        columnIndex: 3,
                    },
                    {
                        name: 'pickupLocation',
                        value: load?.pickupLocation?.replaceAll('to', ';'),
                        columnIndex: 4,
                    },
                    {
                        name: 'dropoffLocation',
                        value: load?.dropoffLocation?.replaceAll('to', ';'),
                        columnIndex: 5,
                    },
                ],
                loadSettlementDetail: load.earnings
                    ?.map((earning: IEarnings) => ({
                        loadEarning: earning.description || '',
                        weight: earning.weight
                            ? earning.weight
                                  ?.toString()
                                  ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            : '0.00',
                        units: earning.units
                            ? earning.units?.toFixed(2)
                            : '0.00',
                        rate: earning.rate ? earning.rate?.toFixed(2) : '0.00',
                        payment: earning.payment?.toFixed(2),
                    }))
                    .concat([
                        {
                            loadEarning: '',
                            weight: '',
                            units: '',
                            rate: '',
                            payment: load.totalEarnings?.toFixed(2),
                        },
                    ]),
            }),
        ),
    }));
};

interface ISettlementReportInitialState {
    loading: boolean;
    settlementReportDetail: SettlementReportData;
    fullSettlementReportDetail: SettlementReportData;
    isSettlementReportEdited: boolean;
    status: APIStatus;
}

const initialState: ISettlementReportInitialState = {
    loading: false,
    settlementReportDetail: [],
    fullSettlementReportDetail: [],
    isSettlementReportEdited: false,
    status: APIStatusOK,
};

export const fetchSettlementReportSlice = createSlice({
    name: 'fetchSettlementReport',
    initialState,
    reducers: {
        setFullSettlementReportDetail: (
            state,
            action: PayloadAction<SettlementReportData>,
        ) => {
            state.fullSettlementReportDetail = action.payload;
        },
        setSettlementReportDetail: (
            state,
            action: PayloadAction<SettlementReportData>,
        ) => {
            state.settlementReportDetail = action.payload;
        },
        setIsSettlementReportEdited: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.isSettlementReportEdited = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchSettlementReport.pending, state => {
            state.loading = true;
        });
        builder.addCase(fetchSettlementReport.fulfilled, (state, action) => {
            state.loading = false;
        });
        builder.addCase(fetchSettlementReport.rejected, (state, action) => {
            state.loading = false;
            state.status = action.payload as APIStatus;
        });
    },
});

export const {
    setSettlementReportDetail,
    setFullSettlementReportDetail,
    setIsSettlementReportEdited,
} = fetchSettlementReportSlice.actions;

const SettlementReportReducer = fetchSettlementReportSlice.reducer;
export default SettlementReportReducer;
